import React, { useEffect, useState, useMemo, useCallback } from "react";
import SubTitle from "../../common/SubTitle";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import DateTabs from "../../manageData/Analytics/DateTabs";
import BarGraph from "../../manageData/Analytics/charts/BarGraph";
import LineGraph from "../../manageData/Analytics/charts/LineGraph";
import PieChart from "../../manageData/Analytics/charts/PieChart";
import axios from "axios";
import config from "../../../config.js";
import { useUser } from "../../../pages/login/UserContextPersistent";

// Graph Card Component (UI-aligned with the reference design)
const GraphCard = ({
  title,
  height = "300px",
  children,
  subtitle,
  showVehicleDropdown = false,
  onVehicleChange,
  selectedVehicle,
  vehicleList = [],
}) => {
  return (
    <Card
      sx={{
        height: "100%",
        boxShadow: " -1px 2px 4px 0px #00000033",
        borderRadius: "12px",
        border: "1px solid #A5DCF3",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "row",
          padding: "16px",
        }}
      >
        <Typography
          sx={{
            fontSize: "20px",
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            fontWeight: 600,
            color: "#333",
            mr: 2,
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showVehicleDropdown && vehicleList.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={selectedVehicle}
                  onChange={(e) => onVehicleChange(e.target.value)}
                  sx={{
                    backgroundColor: "#fff",
                    fontSize: "12px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#03A9F4",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0288D1",
                    },
                  }}
                >
                  {vehicleList.map((vehicle) => (
                    <MenuItem key={vehicle.value} value={vehicle.value} sx={{ fontSize: "12px" }}>
                      {vehicle.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {children}
          </Box>
          {subtitle ? (
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                fontWeight: 600,
                color: "#333",
                mt: 1,
                fontSize: "20px",
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
};

function getOrgId() {
  try {
    return JSON.parse(localStorage.getItem("userState") || "{}").user?.organizationId;
  } catch {
    return null;
  }
}

export default function AnalyticsEntryExit() {
  const navigate = useNavigate();
  const { organizationInactive } = useUser();
  const organizationId = getOrgId();
  const canCreateRecord = !organizationInactive;

  const [viewType, setViewType] = useState(0); // 0 Vehicles, 1 Gates
  const [selectedDateRange, setSelectedDateRange] = useState("today");
  const [dateRange, setDateRange] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedVehicleForGraph, setSelectedVehicleForGraph] = useState("all");

  const vehicleDateOptions = [
    { label: "Today", value: "today" },
    { label: "7 Days", value: "last 7 days" },
    { label: "1 Month", value: "last 1 month" },
    { label: "6 Months", value: "last 6 months" },
    { label: "1 Year", value: "last 1 year" },
    { label: "Custom", value: "custom" },
  ];

  const orgName = useMemo(() => {
    try {
      const userState = localStorage.getItem("userState");
      return userState
        ? JSON.parse(userState)?.organization?.name || "Organization"
        : "Organization";
    } catch {
      return "Organization";
    }
  }, []);

  const fetchAnalytics = useCallback(async (rangeType, range, vehicleId) => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ organizationId: String(organizationId) });

      if (rangeType === "custom" && range?.startDate && range?.endDate) {
        params.set("fromDate", new Date(range.startDate).toISOString().slice(0, 10));
        params.set("toDate", new Date(range.endDate).toISOString().slice(0, 10));
      } else {
        const today = new Date();
        const end = today.toISOString().slice(0, 10);
        let from = end;
        if (rangeType === "yesterday") {
          const d = new Date(today);
          d.setDate(d.getDate() - 1);
          from = d.toISOString().slice(0, 10);
          params.set("toDate", from);
        } else if (rangeType === "last 7 days") {
          const d = new Date(today);
          d.setDate(d.getDate() - 6);
          from = d.toISOString().slice(0, 10);
          params.set("toDate", end);
        } else if (rangeType === "last 1 month") {
          const d = new Date(today);
          d.setDate(d.getDate() - 29);
          from = d.toISOString().slice(0, 10);
          params.set("toDate", end);
        } else if (rangeType === "last 6 months") {
          const d = new Date(today);
          d.setDate(d.getDate() - 179);
          from = d.toISOString().slice(0, 10);
          params.set("toDate", end);
        } else if (rangeType === "last 1 year") {
          const d = new Date(today);
          d.setDate(d.getDate() - 364);
          from = d.toISOString().slice(0, 10);
          params.set("toDate", end);
        } else {
          params.set("toDate", end);
        }
        params.set("fromDate", from);
      }

      if (vehicleId && vehicleId !== "all") params.set("vehicleId", String(vehicleId));

      const res = await axios.get(`${config.baseURL}/vehicle-log/analytics?${params}`, { withCredentials: true });
      setAnalyticsData(res.data);
    } catch {
      setAnalyticsData(null);
    }
    finally { setLoading(false); }
  }, [organizationId]);

  useEffect(() => {
    fetchAnalytics(selectedDateRange, dateRange, selectedVehicleForGraph);
  }, [fetchAnalytics, selectedDateRange, dateRange, selectedVehicleForGraph]);

  const vehicleTypeData = useMemo(
    () =>
      (analyticsData?.vehicleTypeStats || []).map((x) => ({
        type: x.type,
        count: x.total ?? x.inside ?? 0,
      })),
    [analyticsData]
  );
  const gateData = useMemo(
    () =>
      (analyticsData?.gateStats || []).map((x) => ({
        gate: x.gate,
        count: (x.entries ?? 0) + (x.exits ?? 0),
      })),
    [analyticsData]
  );
  const weeklyData = useMemo(() => analyticsData?.dayOfWeekStats || [], [analyticsData]);
  const peakHoursData = useMemo(
    () => (analyticsData?.timeOfDayStats || []).map((x) => ({ time: x.hour, count: x.visits })),
    [analyticsData]
  );
  const pieData = useMemo(
    () => (analyticsData?.vehicleTypeCounts || []).map((x) => ({ name: x.type, value: x.count })),
    [analyticsData]
  );
  const visitedVehicleGateData = useMemo(
    () => {
      const vehicleGateVisits = analyticsData?.vehicleGateVisits || [];
      if (vehicleGateVisits.length > 0) {
        return vehicleGateVisits.map((x) => ({ gate: x.gate, count: x.count }));
      }

      return (analyticsData?.gateStats || []).map((x) => ({
        gate: x.gate,
        count: (x.entries ?? 0) + (x.exits ?? 0),
      }));
    },
    [analyticsData]
  );
  const vehicleList = useMemo(
    () => [
      { value: "all", label: "All Vehicles" },
      ...(analyticsData?.vehiclesList || []).map((v) => ({
        value: String(v.id),
        label: `${v.name}${v.number ? ` (${v.number})` : ""}`,
      })),
    ],
    [analyticsData]
  );
  const selectedVehicleLabel = useMemo(
    () => vehicleList.find((v) => v.value === selectedVehicleForGraph)?.label || "Vehicle",
    [vehicleList, selectedVehicleForGraph]
  );

  const handleDateRangeChange = (rangeType, range) => {
    setSelectedDateRange(rangeType);
    setDateRange(range || null);
  };

  return (
    <>
      <SubTitle
        title="Vehicles Entry/Exit Register"
        path={`${orgName} > Vehicles > Vehicle Entry/Exit Register`}
        add={canCreateRecord ? "+ Create Record" : ""}
        onAddClick={
          canCreateRecord
            ? () =>
                navigate("/vehicle/entry-exit", { state: { openCreateForm: true } })
            : null
        }
        track="Table View"
        trackPath="/vehicle/entry-exit"
        onTrackClick={() => navigate("/vehicle/entry-exit")}
      />

      <Box sx={{ padding: "0px 40px", pb: 4 }}>
        <Box
          sx={{
            height: "52px",
            borderRadius: "8px",
            padding: "12px 24px",
            backgroundColor: "#647490",
            boxShadow: "-2px 2px 5px 0px #00000040",
            fontFamily: "Roboto",
            fontWeight: 600,
            fontSize: "24px",
            color: "#FFFFFF",
          }}
        >
          Analytical View
        </Box>

        <Box sx={{ mt: 3 }}>
          <Tabs
            variant="fullWidth"
            value={viewType}
            onChange={(_, v) => setViewType(v)}
            sx={{
              backgroundColor: "#f5f5f5",
              borderRadius: "8px 8px 0 0",
              "& .MuiTab-root": {
                minWidth: 150,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "16px",
                color: "#000",
              },
              "& .Mui-selected": {
                backgroundColor: "#03A9F4",
                color: "#fff !important",
              },
            }}
          >
            <Tab label="Vehicles" />
            <Tab label="Gates" />
          </Tabs>
        </Box>

        <DateTabs
          onDateRangeChange={handleDateRangeChange}
          selectedDateRange={selectedDateRange}
          dateRangeOptions={vehicleDateOptions}
        />

        {loading ? (
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography>Loading data...</Typography>
          </Box>
        ) : viewType === 0 ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <GraphCard title="Vehicle Still Inside" subtitle="Vehicle Types">
                <BarGraph
                  data={vehicleTypeData}
                  xAxis="type"
                  yAxis="count"
                  title=""
                  color="#03A9F4"
                  height={300}
                  barSize={15}
                />
              </GraphCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <GraphCard
                title={`Visited ${selectedVehicleLabel}`}
                subtitle="Gates"
                showVehicleDropdown={true}
                selectedVehicle={selectedVehicleForGraph}
                onVehicleChange={setSelectedVehicleForGraph}
                vehicleList={vehicleList}
              >
                <BarGraph
                  data={visitedVehicleGateData}
                  xAxis="gate"
                  yAxis="count"
                  title=""
                  color="#4CAF50"
                  height={300}
                  barSize={15}
                />
              </GraphCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <GraphCard title="Avg Duration of Stay (All) Hours" subtitle="Vehicle Types">
                <BarGraph
                  data={analyticsData?.vehicleTypeStats || []}
                  xAxis="type"
                  yAxis="avgDurationHours"
                  title=""
                  color="#FF9800"
                  height={300}
                  barSize={15}
                />
              </GraphCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <GraphCard title="Avg Vehicle Stay (Hours)" subtitle="Days of week">
                <LineGraph
                  data={weeklyData}
                  xAxis="day"
                  yAxis="avgStayHours"
                  title=""
                  color="#9C27B0"
                  height={300}
                  strokeWidth={3}
                />
              </GraphCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <GraphCard title="Avg Stay (Hours)" subtitle="Days of week">
                <BarGraph
                  data={weeklyData}
                  xAxis="day"
                  yAxis="avgStayHours"
                  title=""
                  color="#00BCD4"
                  height={300}
                  barSize={15}
                />
              </GraphCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <GraphCard title="Avg Vehicles Visits Frequency" subtitle="Days of week">
                <LineGraph
                  data={weeklyData}
                  xAxis="day"
                  yAxis="visits"
                  title=""
                  color="#E91E63"
                  height={300}
                  strokeWidth={3}
                />
              </GraphCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <GraphCard title="Avg Vehicles Visit Frequency" subtitle="Time Period">
                <LineGraph
                  data={peakHoursData}
                  xAxis="time"
                  yAxis="count"
                  title=""
                  color="#3F51B5"
                  height={300}
                  strokeWidth={3}
                />
              </GraphCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <GraphCard subtitle="No. of Vehicles Visited">
                <PieChart
                  data={pieData}
                  xAxis="name"
                  yAxis="value"
                  title=""
                  height={200}
                />
              </GraphCard>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <GraphCard title="Vehicles Entered" subtitle="Gates">
                <BarGraph
                  data={(analyticsData?.gateStats || []).map((g) => ({ gate: g.gate, count: g.entries }))}
                  xAxis="gate"
                  yAxis="count"
                  title=""
                  color="#f48403ff"
                  height={300}
                  barSize={15}
                />
              </GraphCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <GraphCard title="Vehicles Exited" subtitle="Gates">
                <BarGraph
                  data={(analyticsData?.gateStats || []).map((g) => ({ gate: g.gate, count: g.exits }))}
                  xAxis="gate"
                  yAxis="count"
                  title=""
                  color="#07b612ff"
                  height={300}
                  barSize={15}
                />
              </GraphCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <GraphCard title="Vehicles Still Inside" subtitle="Gates">
                <BarGraph
                  data={(analyticsData?.gateStats || []).map((g) => ({ gate: g.gate, count: g.inside }))}
                  xAxis="gate"
                  yAxis="count"
                  title=""
                  color="#a803f4ff"
                  height={300}
                  barSize={15}
                />
              </GraphCard>
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  );
}
